import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  ApiRequestService,
  NetSocialAccountFields,
} from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { Router, ActivatedRoute } from '@angular/router';
import {
  AccountService,
  GetAccountsParams,
} from 'netsocial-posting/src/app/services/account/account.service';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import { ModalController, IonContent, PopoverController } from '@ionic/angular';
import { PaginatedItems } from 'netsocial-lib/src/app/interfaces/paginated-items';
import { Account } from 'netsocial-lib/src/app/interfaces/account';
import { JoyrideService } from 'ngx-joyride';
import { UserService } from 'netsocial-posting/src/app/services/user/user.service';
import { computed, observable } from 'mobx';
import { add, sub } from 'date-fns';
import { StorageService } from 'netsocial-lib/src/app/services/storage/storage.service';
import { environment } from '../../../environments/environment';
import { openPostDialog } from '../../utils/post-utils';
import { MainHeaderService } from '../../components/app-shell/main-header/main-header.service';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { BannerService } from '../../services/banners/banner.service';
import { Banner } from 'netsocial-lib/src/app/interfaces/banner';
import { AccountConnectComponent } from '../../components/account/account-connect/account-connect.component';
import { PerformanceGridComponent } from '../../components/dashboard/performance-grid/performance-grid.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.page.html',
  styleUrls: ['./dashboard.page.scss'],
})
export class DashboardPage implements OnInit, OnDestroy {
  @ViewChild(IonContent, { static: false })
  dashboardContainer: IonContent;
  @ViewChild(PerformanceGridComponent, { static: false })
  performanceComponent?: PerformanceGridComponent;

  firstTime = false;
  startDate: Date;
  endDate: Date;
  paginatedAccounts?: PaginatedItems<Account>;
  accountsLoading: boolean;
  numberPendingPosts = '...';
  banners: Banner[];
  bannerDismissed: boolean;

  private contextSubscription: Subscription;
  private tourSubscription: Subscription;

  @computed
  get isAdmin(): boolean | undefined {
    return this.userService.currentUser?.isAdmin;
  }

  @computed
  get performanceView(): string {
    return this.contextOwner?.agency === '1' ? 'organization' : 'personal';
  }

  contextOwner?: NetSocialAccountFields;

  quickLinks = [
    {
      text: 'workspace.dashboard.buttons.createPost',
      icon: 'add-circle',
      click: this.createPost.bind(this),
      createPost: true,
    },
    {
      text: 'workspace.titles.channelsTitle',
      icon: 'images',
      routerLink: '/workspace/content',
    },
    {
      text: 'workspace.dashboard.buttons.viewPending',
      icon: 'alert-circle',
      routerLink: '/workspace/queue/pending',
      onlyMyself: true,
    },
    {
      text: 'helpCenterText',
      icon: 'help-circle',
      click: this.openHelpCenter,
      iconEnd: 'open-outline',
      all: true,
    },
    {
      text: 'User Management',
      icon: 'people',
      routerLink: '/workspace/manage-all',
      agency: true,
    },
    {
      text: 'Client Management',
      icon: 'people-circle',
      routerLink: '/workspace/manage-clients',
      agency: true,
    },
    {
      text: 'Settings',
      icon: 'settings-outline',
      routerLink: '/workspace/settings',
      agency: true,
    },
  ];

  @observable dashboardEndDate = new Date();
  periodOptions = [7, 30, 60, 90];
  viewOptions = ['personal', 'company', 'organization'];
  @observable selectedPeriod = 7;
  @observable selectedView = 'personal';

  @computed
  get dashboardStartDate(): Date {
    return sub(this.dashboardEndDate, { days: this.selectedPeriod });
  }

  @computed
  get isDesktop(): boolean {
    return this.responsiveService.isDesktop;
  }

  @computed //TODO: should be reusable as a util or helper
  get canPost() {
    return this.userService.currentUser?.permissionInfo.permissions.canCreateContent === 'enabled';
  }

  constructor(
    public readonly route: ActivatedRoute,
    public readonly accountService: AccountService,
    public readonly mainHeaderService: MainHeaderService,
    public readonly userService: UserService,
    private readonly apiRequest: ApiRequestService,
    private readonly modalController: ModalController,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly joyrideService: JoyrideService,
    private readonly popover: PopoverController,
    private readonly responsiveService: ResponsiveService,
    private readonly bannerService: BannerService,
    private readonly storage: StorageService
  ) {
    this.startDate = new Date();
    this.endDate = add(new Date(), {
      days: 6,
    });
  }

  ngOnInit(): void {
    this.contextSubscription = this.apiRequest.getContextOwner().subscribe(async (contextOwner) => {
      this.selectedView = '';
      this.viewOptions = [];
      this.contextOwner = contextOwner;
      this.paginatedAccounts = await this.getAccounts();
      this.selectedView =
        this.contextOwner?.agency === '1'
          ? this.contextOwner.name
          : this.contextOwner?.accountType === 'company'
          ? 'organization'
          : 'personal';

      if (this.contextOwner?.agency === '1') {
        this.userService.netSocialAccountsInput?.items.map((account) => {
          this.viewOptions.push(account.name);
        });
      } else {
        this.viewOptions =
          this.contextOwner?.accountType === 'company'
            ? ['company', 'organization']
            : ['personal', 'company', 'organization'];
      }
    });

    this.tourSubscription = this.userService.tourBehaviorSubject
      .asObservable()
      .subscribe((tour) => {
        if (tour && this.performanceComponent?.el.nativeElement) {
          this.dashboardContainer?.scrollToPoint(
            0,
            this.performanceComponent.el.nativeElement.offsetTop
          );
        }
      });
    this.getBanners();
    this.checkBannerDismissal();
  }

  ngOnDestroy(): void {
    this.contextSubscription?.unsubscribe();
    this.tourSubscription?.unsubscribe();
  }

  showDashboard(): void {
    this.firstTime = false;
  }

  openHelpCenter(): void {
    window.open('https://helpme.haleymarketing.com/', '_blank');
  }

  async getBanners(): Promise<void> {
    if (!this.banners) {
      try {
        this.banners = await this.bannerService.getBanners();
      } catch (error) {
        this.errorService.toastError('services.banner.errors.genericError', {
          error: error ? error.status || error.results.xstatus : '',
        });
      }
    }
  }

  async checkBannerDismissal(): Promise<void> {
    if (this.storage) {
      const bannerDismissed = await this.storage.get('bannerDismissed');
      this.bannerDismissed = bannerDismissed ? true : false;
    }
  }

  dismissBanner(): void {
    if (this.storage) {
      this.storage && this.storage.set('bannerDismissed', true);
      this.bannerDismissed = true;
    }
  }

  async getAccounts(first?: number): Promise<PaginatedItems<Account> | undefined> {
    if (!this.paginatedAccounts) {
      this.accountsLoading = true;
    }

    const params: GetAccountsParams = {
      canPost: 1,
      first,
      perPage: environment.perPage,
    };

    const paginatedAccounts = await this.errorService.resolve(
      this.accountService.getAccounts(params),
      {
        errorMessage: 'components.accountList.errors.getAccounts',
      }
    );

    this.accountsLoading = false;
    return paginatedAccounts;
  }

  async connectAccount(): Promise<void> {
    const modal = await this.modalController.create({
      component: AccountConnectComponent,
      cssClass: 'account-dialog',
    });

    return await modal.present();
  }

  async createPost(): Promise<void> {
    if (this.paginatedAccounts?.items.length) {
      const accountsToPostTo = [this.paginatedAccounts.items[0]];

      const modal = await openPostDialog(this.modalController, 'post-dialog', {
        accountsToPostTo,
        paginatedAccounts: this.paginatedAccounts,
      });

      return await modal.present();
    } else {
      this.errorService.toastMessage('connectAccountText');
    }
  }

  queueActionFunction = async (): Promise<void> => {
    this.router.navigateByUrl('/workspace/queue');
  };

  async openQuickTour(event: Event): Promise<void> {
    this.mainHeaderService.openTourStatus(event, this.popover);
  }

  openPendingPosts = async (): Promise<void> => {
    this.router.navigateByUrl('/workspace/queue/pending');
  };

  startTour(): void {
    this.joyrideService.startTour({
      steps: ['performance', 'topPosts'],
      showPrevButton: false,
    });
  }

  async instagramButton(): Promise<void> {
    const modal = await this.modalController.create({
      component: AccountConnectComponent,
      cssClass: 'custom-class',
    });

    return await modal.present();
  }
}
